import { useAsyncData } from '#imports'

import type { ProductsBlockApiProps } from '@backmarket/http-api/src/api-specs-content/models/products-block'
import type { RefinedProductsApiProps } from '@backmarket/http-api/src/api-specs-content/models/refined-products'
import type { Granularity } from '@backmarket/nuxt-layer-recommendation/models/product'
import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import algoliasearch from 'algoliasearch'

import { mapSingleAlgoliaHitToProduct } from './algolia/algoliaAdapter'
import type { AlgoliaProductHit } from './algolia/types'
import {
  type InitializableAlgoliaClient,
  useAlgoliaWithDeps,
} from './algolia/useAlgolia'
import type { SortAlgoliaHitsFn } from './models/sort-alglia-hits-fn'

type Params =
  | Pick<ProductsBlockApiProps, 'productQueries'>
  | Pick<RefinedProductsApiProps, 'productQuery'>

function buildAlgoliaParams(props: Params) {
  const productQueries =
    'productQueries' in props ? props.productQueries : [props.productQuery]

  const { apiKey } = productQueries[0]

  const queries = productQueries.map(({ indexName, params }) => ({
    indexName,
    params,
  }))

  return { apiKey, queries }
}

export const useSearchProductsWithDeps =
  (initializableAlgoliaClient: InitializableAlgoliaClient<AlgoliaProductHit>) =>
  (
    props: Params,
    {
      server = true,
      immediate = true,
      productGranularity,
      sort,
    }: {
      server?: boolean
      immediate?: boolean
      productGranularity?: Granularity
      sort?: SortAlgoliaHitsFn
    } = {},
  ) => {
    const filter =
      'productQuery' in props ? props.productQuery.params.filters : ''
    const uniqueKey = getAsyncDataKey(`cms-products-${filter}`, props)

    const { search } = useAlgoliaWithDeps({
      initializableAlgoliaClient,
      algoliaSearchOptionType: 'search',
    })

    return useAsyncData(
      uniqueKey,
      async () => {
        const algoliaParams = buildAlgoliaParams(props)

        const algoliaResponse = await search(algoliaParams)

        const algoliaHits = algoliaResponse.results.flatMap(
          (result) => result.hits,
        )
        const sortedAlgoliaHits = sort
          ? algoliaHits.toSorted(sort)
          : algoliaHits

        return sortedAlgoliaHits.map((hit) =>
          mapSingleAlgoliaHitToProduct(hit, productGranularity),
        )
      },
      {
        getCachedData(key, nuxtApp) {
          const cachedData = nuxtApp.payload.data[key]

          // do not return cached data if it's an empty array
          // this allows the "Try again" feature in EnhancedCarousel to work
          if (Array.isArray(cachedData) && cachedData.length === 0)
            return undefined

          return cachedData
        },
        server,
        immediate,
      },
    )
  }

export function useSearchProducts(
  params: Params,
  opts: {
    server?: boolean
    immediate?: boolean
    productGranularity?: Granularity
    sort?: SortAlgoliaHitsFn
  },
) {
  return useSearchProductsWithDeps(
    algoliasearch as InitializableAlgoliaClient<AlgoliaProductHit>,
  )(params, opts)
}
